import { useAtom } from "jotai";
import type { JSX } from "react";

import { graphql } from "@repo/graphql-types/gql";
import { CodFluxoEtapaStatus } from "@repo/graphql-types/graphql";

import { ProfessionWithCouncilSubStep } from "@/components/flows/steps/council/components/profession-with-council-sub-step";
import {
  ALL_PROFESSIONS,
  PROFESSIONS_WITH_CERTIFICATE,
} from "@/components/flows/steps/council/utils/professions";
import { ProgressRoundedLayout } from "@/components/ui/layout/progress-rounded-layout";
import { useGraphQL } from "@/hooks/use-graphql";
import { useSubStepNavigation } from "@/hooks/use-sub-step-navigation";
import { councilStepAtom } from "@/lib/atoms/council-step-atom";
import { useFlowStepContext } from "@/lib/context/flow-step-context";
import { getIP } from "@/lib/ip-getter";
import { flattenToJsonString } from "@/lib/utils/flatten-to-json-string";

const CouncilStepQuery = graphql(/* GraphQL */ `
  query TrainingOrCouncilValidationStepQuery {
    UFs: tbUFs {
      siglaUF
      nome
    }
  }
`);

export const CouncilPage = (): JSX.Element => {
  const { step, nextStep, isLoading, progress } = useFlowStepContext();
  const [councilState, setCouncilState] = useAtom(councilStepAtom);
  const { goToSubStep } = useSubStepNavigation();
  const { data } = useGraphQL(CouncilStepQuery);

  const ufOptions = (data?.UFs ?? []).map((item: { siglaUF: string; nome: string }) => ({
    label: `${item.siglaUF} - ${item.nome}`,
    value: item.siglaUF,
  }));

  const handleSubmit = async () => {
    const { profession, professionName, councilNumber, uf } = councilState;
    const resolvedProfessionName =
      professionName ??
      ALL_PROFESSIONS.find((p) => p.value === profession)?.label ??
      null;
    const councilAcronym =
      councilState.councilAcronym ??
      ALL_PROFESSIONS.find((p) => p.value === profession)?.council ??
      null;
    if (profession && PROFESSIONS_WITH_CERTIFICATE.has(profession)) {
      goToSubStep("terms");
      return;
    }
    const ipAddress = await getIP();
    void nextStep({
      ...step,
      resposta: flattenToJsonString({
        profession,
        professionName: resolvedProfessionName,
        councilNumber,
        councilAcronym,
        uf,
        ipAddress,
      }),
      status: CodFluxoEtapaStatus.Concluido,
    });
  };

  const handleSkip = () => {
    setCouncilState((s) => ({ ...s, councilNumber: "", uf: "" }));
    const { profession, professionName } = councilState;
    const resolvedProfessionName =
      professionName ??
      ALL_PROFESSIONS.find((p) => p.value === profession)?.label ??
      null;
    const councilAcronym =
      councilState.councilAcronym ??
      ALL_PROFESSIONS.find((p) => p.value === profession)?.council ??
      null;
    void nextStep({
      ...step,
      resposta: flattenToJsonString({
        profession,
        professionName: resolvedProfessionName,
        councilNumber: "",
        councilAcronym,
        uf: "",
      }),
      status: CodFluxoEtapaStatus.Concluido,
    });
  };

  const councilName =
    ALL_PROFESSIONS.find((p) => p.value === councilState.profession)?.council ??
    "Conselho";

  return (
    <ProgressRoundedLayout
      progress={progress}
      onBack={() => {
        goToSubStep("main");
      }}
    >
      <ProfessionWithCouncilSubStep
        councilNumber={councilState.councilNumber}
        onCouncilNumberChange={(value) => {
          setCouncilState((s) => ({ ...s, councilNumber: value }));
        }}
        councilName={councilName}
        uf={councilState.uf}
        onUfChange={(value) => {
          setCouncilState((s) => ({ ...s, uf: value }));
        }}
        ufOptions={ufOptions}
        title={step.configuracoes?.tituloConselho ?? ""}
        description={step.configuracoes?.descricaoConselho ?? ""}
        buttonText={step.configuracoes?.textoBotaoConselho ?? "Continuar"}
        skipButtonText={
          step.configuracoes?.textoBotaoPular ?? "Ainda não tenho conselho ativo"
        }
        isLoading={isLoading}
        onSubmit={handleSubmit}
        onSkip={handleSkip}
      />
    </ProgressRoundedLayout>
  );
};
